import { ApiError, NotAuthenticatedError } from '@/lib/errors'
import { getTokenStore } from '@/lib/tokenStore'
import { refreshAccessToken, RefreshTokenNotExistError } from '@/lib/authTokenRefresh'

// 埋め込みスクリプトの場合、環境変数が読み込まれないため、
// スクリプトタグのsrc属性から動的にbaseURLを推定する
function getBaseURL(): string {
  // ✅ SSR（サーバ）ではdocument/windowが無いので触らない
  // コンテナ間通信用のエンドポイント（例: http://api:3000）を優先し、
  // なければブラウザ向けエンドポイントにフォールバック
  if (typeof window === 'undefined') {
    const ssrUrl = process.env['NEXT_PUBLIC_ORVAL_ENDPOINT'] ?? process.env['NEXT_PUBLIC_API_ENDPOINT'] ?? 'http://localhost:8000'
    console.log('[customFetch SSR] baseURL:', ssrUrl, 'ORVAL_ENDPOINT:', process.env['NEXT_PUBLIC_ORVAL_ENDPOINT'], 'API_ENDPOINT:', process.env['NEXT_PUBLIC_API_ENDPOINT'])
    return ssrUrl
  }

  // 環境変数が利用可能な場合はそれを使用
  if (process.env['NEXT_PUBLIC_API_ENDPOINT']) {
    return process.env['NEXT_PUBLIC_API_ENDPOINT']
  }

  // ✅ 安全面：埋め込みスクリプトのホストを許可リストで制限する（まずローカルだけ）
  const ALLOWED_API_HOSTS = new Set(['localhost', '127.0.0.1'])

  // 埋め込みスクリプトの場合、スクリプトタグのsrcからbaseURLを推定（ブラウザのみ）
  const scriptElement =
    (document.getElementById('gai-agents-embed-script') ??
      document.querySelector('script[src*="embedded.js"]')) as HTMLScriptElement | null

  if (scriptElement?.src) {
    const scriptUrl = new URL(scriptElement.src)

    // ✅ 許可されていないホストなら推定しない
    if (!ALLOWED_API_HOSTS.has(scriptUrl.hostname)) {
      return 'http://localhost:8000'
    }

    // 例: http://localhost:3000 → http://localhost:8000
    const port = scriptUrl.hostname === 'localhost' ? '8000' : scriptUrl.port
    return `${scriptUrl.protocol}//${scriptUrl.hostname}${port ? ':' + port : ''}`
  }

  // フォールバック: 現在のページのプロトコルとホストを使用（windowは必ずある）
  const currentUrl = window.location
  const port = currentUrl.hostname === 'localhost' ? '8000' : currentUrl.port
  return `${currentUrl.protocol}//${currentUrl.hostname}${port ? ':' + port : ''}`
}


const baseURL = getBaseURL()

async function redirect(reason?: string): Promise<never> {
  // Clear all data from IndexedDB
  const tokenStore = getTokenStore()
  await tokenStore.clearAll()
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : ''
  window.location.href = `/sign-in${qs}`
  throw new NotAuthenticatedError()
}

// レスポンスbodyの中に code === 'SESSION_INVALIDATED' があるかを判定する
async function isSessionInvalidatedResponse(response: Response): Promise<boolean> {
  try {
    const body = await response.clone().json()
    return body && typeof body === 'object' && (body as { code?: string }).code === 'SESSION_INVALIDATED'
  } catch {
    return false
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function parse(response: Response): Promise<any> {
  if (response.status === 204) {
    return undefined
  }

  const contentType = response.headers.get('Content-Type')

  if (contentType?.startsWith('application/json')) {
    return response.json()
  } else {
    return response.blob()
  }
}

export default async function customClient<Return, BodyData = unknown>({
  url: path,
  method,
  params,
  headers = {},
  data,
}: // responseType,
{
  url: string
  method: string
  params?: Record<string, string | number | boolean>
  headers?: Record<string, string>
  data?: BodyData
  responseType?: string
}): Promise<Return> {
  const tokenStore = getTokenStore()
  const searchParams = params
    ? `?${new URLSearchParams(Object.entries(params).map(([key, value]) => [key, value.toString()]))}`
    : ''
  // build a url
  const url = new URL(`${baseURL}${path}${searchParams}`)

  // 一部のエンドポイントは、タブが閉じてもリクエストが中断されないようにする
  // keepalive はリクエストバイト数に上限(64KiB)があるので注意すること
  const keepalive = path.startsWith('/public/lead-session-events') && method === 'POST'

  // get tokens to authorize
  let accessToken = await tokenStore.getAccessToken()
  if (!accessToken && (await tokenStore.getRefreshToken())) {
    accessToken = await refreshAccessToken().catch(() => null)
  }
  // 未認証の場合
  if (!accessToken) {
    // TODO: 未認証でAPIコールするのはpublicエンドポイントだけなので、
    // publicエンドポイント用のクライアントを新しく作成し moovyPublic.ts からはそちらを参照するようにして、
    // このクライアントからは分岐を削除する
    const response = await fetch(url, {
      keepalive,
      method,
      headers,
      ...(data && { body: JSON.stringify(data) }),
    })

    // 成功
    if (response.ok) {
      return parse(response)
    }

    // 401 の場合はリダイレクト
    if (response.status === 401) {
      redirect()
    }

    // エラー
    const detail = await response.clone().json().then((body: unknown) => {
      if (body && typeof body === 'object' && 'detail' in body) {
        return (body as { detail: string }).detail
      }
      return undefined
    }).catch(() => undefined)
    throw new ApiError(response, detail)
  }

  // 認証済みの場合
  // アクセストークンを付与
  headers['Authorization'] = `Bearer ${accessToken}`
  // システム管理者がテナントの切替を行っている場合はヘッダーを追加
  const tenantId = await tokenStore.getTenantId()
  if (tenantId) {
    headers['X-tenant-id'] = tenantId
  }
  // 単一セッション強制用のヘッダーを付与
  // sessionIdが存在しないログイン直後の POST /sessions では何も付かないため安全
  const sessionId = await tokenStore.getSessionId()
  if (sessionId) {
    headers['X-Session-Id'] = sessionId
  }

  // リクエスト
  const response = await fetch(url, {
    keepalive,
    method,
    headers,
    ...(data && { body: JSON.stringify(data) }),
  })

  // 成功
  if (response.ok) {
    return parse(response)
  }

  // 401 以外はエラー
  if (response.status !== 401) {
    if (response.status === 403 && tenantId) {
      await tokenStore.removeTenantId()
    }
    const errorDetail = await response.clone().json().then((body: unknown) => {
      if (body && typeof body === 'object' && 'detail' in body) {
        return (body as { detail: string }).detail
      }
      return undefined
    }).catch(() => undefined)
    throw new ApiError(response, errorDetail)
  }

  // 401 の場合
  // body.code === 'SESSION_INVALIDATED' なら他端末ログインによるキックなので
  // refreshTokenを試みずに即リダイレクトする
  if (await isSessionInvalidatedResponse(response)) {
    redirect('session_invalidated')
  }

  // 通常の 401 はトークンをリフレッシュしてリトライを試みる
  // 同時実行制御のため、グローバルに Promise を保持する
  const newAccessToken = await refreshAccessToken().catch((e) => {
    // refresh token が存在しない場合は異常事態なので、例外を投げる
    if (e instanceof RefreshTokenNotExistError) {
      throw e
    }
    // それ以外のエラーはリダイレクト
    redirect()
  })

  headers['Authorization'] = `Bearer ${newAccessToken}`
  // リトライ時にもセッションIDが変わっている可能性があるので再取得する
  const retrySessionId = await tokenStore.getSessionId()
  if (retrySessionId) {
    headers['X-Session-Id'] = retrySessionId
  }

  const retryResponse = await fetch(url, {
    keepalive,
    method,
    headers,
    ...(data && { body: JSON.stringify(data) }),
  })

  // 成功
  if (retryResponse.ok) {
    return parse(retryResponse)
  }

  // 再度 401 の場合は諦めてリダイレクト
  if (retryResponse.status === 401) {
    if (await isSessionInvalidatedResponse(retryResponse)) {
      redirect('session_invalidated')
    }
    redirect()
  }

  // 401 以外は例外
  const retryDetail = await retryResponse.clone().json().then((body: unknown) => {
    if (body && typeof body === 'object' && 'detail' in body) {
      return (body as { detail: string }).detail
    }
    return undefined
  }).catch(() => undefined)
  throw new ApiError(retryResponse, retryDetail)
}
